/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useEffect } from "react";

interface ThrottleOptions {
  leading?: boolean;
  trailing?: boolean;
}

interface ThrottleResult<T extends (...args: any[]) => any> {
  run: (...args: Parameters<T>) => void;
  cancel: () => void;
  flush: () => void;
}

export function useThrottleFn<T extends (...args: any[]) => any>(
  fn: T,
  wait: number,
  options: ThrottleOptions = { leading: true, trailing: true },
): ThrottleResult<T> {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastArgsRef = useRef<Parameters<T> | null>(null);
  const lastTimeRef = useRef<number>(0);
  const fnRef = useRef<T>(fn);

  // keep the function ref up to date
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    lastArgsRef.current = null;
  }, []);

  // cleanup on unmount
  useEffect(() => {
    return cancel;
  }, [cancel]);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (lastArgsRef.current) {
      fnRef.current(...lastArgsRef.current);
      lastArgsRef.current = null;
      lastTimeRef.current = Date.now();
    }
  }, []);

  const run = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const elapsed = now - lastTimeRef.current;

      // cancel any pending executions
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // handle leading edge
      if (!lastTimeRef.current && options.leading) {
        fnRef.current(...args);
        lastTimeRef.current = now;
        return;
      }

      //Handle case where we're still waiting
      if (elapsed < wait) {
        //schedule trailing edge execution
        if (options.trailing) {
          lastArgsRef.current = args;
          timeoutRef.current = setTimeout(() => {
            fnRef.current(...args);
            lastTimeRef.current = Date.now();
            lastArgsRef.current = null;
            timeoutRef.current = null;
          }, wait - elapsed);
        }
        return;
      }

      // Handle normal execution
      lastTimeRef.current = now;
      fnRef.current(...args);
      lastArgsRef.current = null; // reset after execution
    },
    [wait, options.leading, options.trailing],
  );

  return { run, cancel, flush };
}
